/**
 * Состав бокового меню — по процессам (решение владельца 26.09,
 * ~/Desktop/Разделы_и_меню_26.09.md, раздел 4).
 *
 * Пункт меню = процесс, внутри — вкладки. Каждая вкладка — прежний экран по
 * прежнему адресу и с прежними правами: ссылки из бота, базы знаний и закладки
 * работают как раньше.
 *
 * Как устроено:
 *  - строка ниже — одна ВКЛАДКА (бывший пункт меню): path, label (название
 *    вкладки), section/обёртки admin()/staff() — права, как были у пункта;
 *  - proc — название процесса: вкладки с одинаковым proc в одной группе
 *    склеиваются в один пункт меню (buildMenuEntries). Без proc — пункт сам по себе;
 *  - was — прежнее название пункта: по нему находит поиск (Ctrl/⌘+K, «Все
 *    разделы»), и под ним вкладка стоит в «Моей работе» кладовщика;
 *  - пункт процесса виден, если видна хоть одна его вкладка, и ведёт на первую
 *    видимую; над страницей вкладки процесса рисует Layout (GroupTabs);
 *  - action — не экран, а действие («Создать отгрузку»): в меню не стоит, пока
 *    виден экран menuFallback (там кнопка), но всегда находится поиском;
 *  - права: section у вкладки + canOpen(адрес) — та же таблица, что и маршруты.
 *    Бывшие admin()-пункты (05.10) — staff() с разделом: их видит тот, кому
 *    выдали галочку, а не только администратор.
 *
 * Отдельный блок «Продажи и прочее» (CRM, калькуляторы, редактор БЗ) владелец
 * велел не сливать: он ждёт полной переделки перед коммерческим запуском.
 * «Баркоды» 27.09 переехали в «Товары», «Данные системы» убраны.
 *
 * keywords — слова для поиска, которых нет в названии/подсказке.
 */
import {
  Building2, Package, Barcode, ClipboardList, BarChart3, Users, Warehouse, Boxes,
  PackageCheck, ScanBarcode, PackageOpen, ScanLine, Wrench, ShoppingBag,
  Store, PieChart, Settings2, Banknote, Grid3X3, MapPin, Printer, Truck, PlusCircle,
  Tags, Key, HelpCircle, History, AlertTriangle, Bell, Undo2, Calculator, Receipt,
  Inbox, Briefcase, Contact, Activity, Cpu, Shield, Smartphone, Search, UserPlus,
  LayoutGrid, BookOpen, Layers, Palette, FileText, Rocket, Blocks, ArrowRightLeft,
} from 'lucide-react';
import { Cloud, CreditCard, Sparkles, Wallet, ClipboardCheck, Megaphone } from 'lucide-react';     // облако Н3: кабинет платформы; подписка

// Меню склада и администратора: группы по ходу работы, внутри — процессы
// isCloud — склад облака (бренд не Full-Fix): у него свой раздел «Бренд и домен»
// sellerCloud — склад облака без клиентов («Склад селлера»): товар свой, компания одна
// (магазин), поэтому «Клиенты» → «Мой магазин» и подсказки без клиентов.
// Склад селлера: группа «Клиенты и деньги» — это его магазин (биллинг клиентов выключен
// модулем), «Клиенты» → «Мой магазин». Пункты и права те же — меняются только подписи.
function sellerLabels(nav) {
  return nav.map((sec) => (sec.label !== 'Клиенты и деньги' ? sec : {
    ...sec,
    label: 'Магазин',
    items: sec.items.map((it) => (it.path !== '/companies' ? it : {
      ...it, label: 'Мой магазин', hint: 'Магазин: название, ИНН, подключения к маркетплейсам', keywords: 'клиенты компания',
    })),
  }));
}

export function buildStaffNav({ isAdmin, isInternalStaff, canFinance, user, isCloud, isPlatformOwner, sellerCloud }) {
  const admin = (item) => (isAdmin ? [item] : []);
  const sel = (fullfixText, sellerText) => (sellerCloud ? sellerText : fullfixText);
  const staff = (item) => (isInternalStaff ? [item] : []);
  const nav = [
    {
      label: 'Сегодня',
      items: [
        ...staff({ path: '/work', label: 'Моя смена', was: 'Мои задачи', hint: 'С чего начать смену: сколько дел на каждом участке', icon: ClipboardList }),
        ...staff({ path: '/tasks', label: 'Задачи', hint: 'Поручения сотрудникам и задачи клиентов складу: взять, сделать, фото', icon: ClipboardCheck, section: 'tasks', keywords: 'поручения задачи клиентов' }),
        { path: '/board', label: 'Доска', proc: 'Склад сейчас', was: 'Склад сейчас', hint: 'Весь склад на одной доске: что принимаем, что собираем, что уезжает', icon: Grid3X3, section: 'board' },
        // Складу /dashboard — сводка по отгрузкам (стадии, черновики)
        { path: '/dashboard', label: 'Стадии отгрузок', proc: 'Склад сейчас', was: 'Сводка отгрузок', hint: 'Отгрузки по стадиям, черновики, что застряло', icon: BarChart3, section: 'processing' },
        ...staff({ path: '/shift', label: 'Кто на смене', proc: 'Смена', was: 'Смена', hint: 'Кто сегодня на смене и где работает', icon: Contact, section: 'shift' }),
        ...staff({ path: '/temp-workers', label: 'Подработчики', proc: 'Смена', hint: 'Записались на смену через бота и сайт: пришёл, бейдж, оценка', icon: UserPlus, section: 'temp_workers' }),
        ...staff({ path: '/monitor', label: 'Живая лента', proc: 'Смена', was: 'Монитор склада', hint: 'Что происходит на складе прямо сейчас', icon: Activity, section: 'activity' }),
        ...staff({ path: '/analytics', label: 'По отгрузкам', proc: 'Аналитика', was: 'Аналитика', icon: PieChart, section: 'analytics' }),
        ...staff({ path: '/warehouse-activity', label: 'Работа склада', proc: 'Аналитика', hint: 'Сколько сделали, с какой выработкой, кто и по каким клиентам', icon: Activity, section: 'activity' }),
      ],
    },
    {
      label: 'Приёмка',
      items: [
        { path: '/arrivals', label: 'Приёмки', proc: 'Приёмки', was: 'Приёмка', hint: sel('Приёмки товара от клиентов: план, факт, короба', 'Приёмки товара: план, факт, короба'), icon: PackageCheck, section: 'arrivals' },
        { path: '/pickups', label: 'Забор', proc: 'Приёмки', hint: sel('Заявки на забор товара у клиента', 'Заявки на забор товара у поставщика'), icon: Truck, section: 'arrivals' },
        { path: '/pvz-returns', label: 'Возвраты ПВЗ', proc: 'Приёмки', hint: 'Товар, вернувшийся из пунктов выдачи', icon: Undo2, section: 'arrivals' },
        { path: '/box-placement', label: 'Короба', proc: 'Размещение', was: 'Размещение коробов', hint: 'Палета = ячейка: скан короба, потом скан ячейки', icon: PackageOpen, section: 'placement' },
        // Приёмка без коробов кладёт товар в ячейку приёмки — разносить по полкам только здесь
        { path: '/placement', label: 'Штучно', proc: 'Размещение', was: 'Размещение товара (штучно)', hint: 'Приёмка без коробов: разнести товар из ячейки приёмки по полкам', icon: MapPin, section: 'placement' },
      ],
    },
    {
      label: 'Склад',
      items: [
        { path: '/where-is', label: 'Где лежит товар', hint: 'Все места единицы товара: ячейки, короба, адреса кодов ЧЗ', icon: Search, section: 'scanning' },
        { path: '/products', label: 'Каталог', proc: 'Товары', was: 'Товары', hint: sel('Каталог товаров клиентов', 'Каталог товаров магазина'), icon: ShoppingBag, section: 'products' },
        // «Остатки» — вкладка «Товаров», а не отдельный раздел (решение владельца 01.10:
        // два почти одинаковых раздела). Адрес /warehouse прежний, страница та же.
        { path: '/warehouse', label: 'Остатки', proc: 'Товары', was: 'Остатки', hint: 'Сколько товара свободно, в брони и в работе — по складам и по ячейкам', icon: Boxes, section: 'warehouse' },
        ...staff({ path: '/stock-log', label: 'Движение', proc: 'Товары', was: 'Лог остатков', hint: 'История движения остатков', icon: History, section: 'stock_log' }),
        ...staff({ path: '/stock-transfer', label: 'Перенос остатка', proc: 'Товары', hint: 'Перекинуть остаток с одной карточки на другую (дубль карточки, новый клиент)', icon: ArrowRightLeft, section: 'stock_writeoff', keywords: 'перенос перемещение карточка дубль остаток переброс' }),
        { path: '/product-management', label: 'Загрузка Excel', proc: 'Товары', was: 'Управление товарами', hint: 'Загрузка и массовое изменение товаров из Excel', icon: Settings2, section: 'product_import' },
        // Перенесено из «Продаж» (решение владельца 27.09): действие каталога, а не раздел
        { path: '/barcodes', label: 'Баркоды в Excel', proc: 'Товары', was: 'Баркоды', hint: 'Дописать колонку баркода в свой Excel по артикулу и размеру', icon: Barcode, section: 'barcodes', keywords: 'штрихкоды шк' },
        { path: '/storage', label: 'Ячейки', proc: 'Ячейки', hint: 'Схема склада: зоны, стеллажи, ячейки хранения', icon: Grid3X3, section: 'storage' },
        ...staff({ path: '/warehouse-builder', label: 'Конструктор склада', proc: 'Ячейки', hint: 'Нарисовать склад: зоны, стеллажи, полки — адреса и этикетки создадутся сами', icon: LayoutGrid, section: 'storage', keywords: 'схема склада зоны стеллажи полки адреса этикетки' }),
        { path: '/defects', label: 'Журнал', proc: 'Брак', was: 'Брак / Пересорт', hint: 'Товар с дефектом или пересортица — оформление и списание', icon: AlertTriangle, section: 'defects' },
        ...staff({ path: '/client-brak', label: 'В ячейках брака', proc: 'Брак', was: 'Брак клиентов', hint: 'Что лежит в ячейках брака по каждому клиенту — вне остатка, не продаётся', icon: AlertTriangle, section: 'storage' }),
        { path: '/pallet-audit-confirm', label: 'Расхождения', proc: 'Сверки', was: 'Сверки и расхождения', hint: 'Где учёт разошёлся с ячейками по всему складу; списания по сверкам на решение руководителя', icon: AlertTriangle, section: 'pallet_audit_confirm' },
        // Один экран, две внутренние вкладки: ячейки закрытых документов и сверка палеты
        { path: '/cell-audit', label: 'Ячейки и палеты', proc: 'Сверки', was: 'Сверка ячеек и палет', hint: 'Вещи в ячейках закрытых документов — вернуть на полку; перекличка коробов палеты — вкладкой', icon: ScanBarcode, keywords: 'сверка палеты вернуть из ячеек отгрузки закрытых документов otg' },
      ],
    },
    {
      label: 'Отгрузки',
      items: [
        // «Заявки на отгрузку» и «Создать поставку» ушли из меню (решение владельца
        // 26.09): заявка и отгрузка создаются одним действием, создание — кнопкой
        // «+ Создать отгрузку» на этом экране. «Подбор» и «Сканирование» тоже
        // убраны: скан и лист подбора — в карточке отгрузки. Адреса работают.
        { path: '/processing', label: 'Все отгрузки', was: 'Отгрузки', hint: 'Поставки на маркетплейсы: стадии, короба, этикетки; «+ Создать отгрузку»', icon: ClipboardList, section: 'processing', keywords: 'заявки на отгрузку' },
        { path: '/orders/create', label: 'Создать отгрузку', action: true, menuFallback: '/processing', hint: 'Новая отгрузка: ФБО, самовывоз, выкладка ФБС, списание', icon: PlusCircle, section: 'orders', keywords: 'создать поставку новая заявка' },
        { path: '/trucks', label: 'Рейсы', hint: 'Машины и какие поставки в них едут', icon: Truck, section: 'trucks' },
      ],
    },
    {
      label: 'Маркировка',
      items: [
        { path: '/marking', label: 'Коды', proc: 'Коды ЧЗ', was: 'Коды ЧЗ', hint: 'Честные знаки: загрузка, печать, привязка к товару', icon: ScanBarcode, section: 'marking', keywords: 'маркировка чз киз честный знак' },
        ...staff({ path: '/kiz-trace', label: 'Где коды', proc: 'Коды ЧЗ', was: 'Коды ЧЗ: где что', hint: 'Где числятся коды по каждому артикулу и вся история конкретной единицы', icon: History, keywords: 'чз киз', section: 'kiz' }),
        { path: '/reprint', label: 'Повторная печать', hint: 'Перепечатать этикетку, если потерялась или испортилась', icon: Printer, section: 'marking' },
      ],
    },
    // Весь ФБС — по ходу процесса: заказы → сборка → зона → контроль
    {
      label: 'ФБС',
      items: [
        ...staff({ path: '/fbs-hub', label: 'Центр ФБС', hint: 'Вся работа ФБС на одной странице: что горит и входы во все инструменты', icon: LayoutGrid }),
        { path: '/fbs', label: 'WB', proc: 'Заказы ФБС', was: 'Заказы ФБС', hint: 'Заказы с маркетплейсов, которые мы собираем и отвозим сами', icon: Package, section: 'processing' },
        ...staff({ path: '/ozon-fbs', label: 'Ozon', proc: 'Заказы ФБС', was: 'Ozon ФБС', hint: 'Заказы Ozon, которые собираем сами: кабинеты, склады, сроки и проблемные', icon: Package }),
        { path: '/fbs-dashboard', label: 'Сроки и машины', proc: 'Заказы ФБС', was: 'ФБС Контроль', hint: 'Успеваем ли собрать заказы к выезду машины', icon: BarChart3, section: 'processing' },
        { path: '/fbs-unaccepted', label: 'Не принято WB', proc: 'Заказы ФБС', hint: 'Заказы, которые у нас отгружены, а Wildberries их не получил', icon: AlertTriangle, section: 'processing' },
        { path: '/fbs-assembly', label: 'Сборка ФБС', hint: 'Скан честного знака → QR заказа для наклейки', icon: ScanLine, section: 'scanning' },
        { path: '/fbs-zone-intake', label: 'Пополнить', proc: 'Работа в зоне', procIcon: Layers, was: 'Пополнение зоны', hint: 'Положить товар в зону ФБС: скан кодов Честного Знака и постановка короба в ячейку', icon: ScanLine, section: 'fbs_zone' },
        { path: '/fbs-move', label: 'Переставить', proc: 'Работа в зоне', was: 'Перемещение', hint: 'Переставить короб или свести разбросанный товар в одну ячейку', icon: PackageOpen, section: 'placement' },
        { path: '/box-recount', label: 'Пересчитать короб', proc: 'Работа в зоне', was: 'Сверка коробов', hint: 'Пересчитать содержимое короба сканом', icon: PackageOpen, section: 'placement' },
        { path: '/fbs-tails', label: 'Хвосты', proc: 'Работа в зоне', hint: 'Что буфер держит от продажи: пропикать, подтвердить, выпустить на WB', icon: PackageOpen, section: 'fbs_zone', keywords: 'нет продаж' },
        { path: '/fbs-return-buffer', label: 'Вернуть на Основной', proc: 'Работа в зоне', was: 'Возврат из зоны', hint: 'Снято с ФБС, а лежит в зоне без полки — разложить на Основной со сканом ячейки', icon: Shield, section: 'placement' },
        { path: '/fbs-zone-control', label: 'Контроль', proc: 'Контроль зоны', was: 'Контроль зоны', hint: 'Что не размещено, разъезд адресов, сходится ли учёт с адресами', icon: Shield, section: 'placement' },
        ...staff({ path: '/zone-ledger', label: 'Реестр', proc: 'Контроль зоны', was: 'Реестр зоны', hint: 'Что вошло в зону, что уехало и что должно лежать — поштучно по кодам ЧЗ, против учёта', icon: History, section: 'zone_ledger' }),
        { path: '/fbs-recount', label: 'Переучёт', proc: 'Контроль зоны', was: 'Переучёт зоны', hint: 'Пересчитать всю зону клиента: заморозка остатка и возврат по сканам', icon: PackageCheck, section: 'fbs_zone' },
      ],
    },
    {
      label: 'Клиенты и деньги',
      items: [
        ...staff({ path: '/companies', label: 'Клиенты', was: 'Компании', hint: 'Клиенты склада: реквизиты, пользователи, настройки', icon: Building2, section: 'companies' }),
        ...staff({ path: '/admin/finance', label: 'Финансы клиентов', hint: 'Балансы, начисления и акты клиентов', icon: Banknote, keywords: 'финансы (админ)', section: 'billing' }),
        ...(canFinance ? [{ path: '/finances', label: 'Финансы склада', was: 'Финансы', hint: 'Доходы, расходы, зарплата', icon: Banknote }] : []),
        ...(canFinance ? [{ path: '/money', label: 'Деньги', hint: 'Счета, операции, переводы, остатки', icon: Wallet, keywords: 'деньги счета операции расходы доходы adesk адеск' }] : []),
        ...staff({ path: '/services', label: 'Услуги', proc: 'Прайс и ТЗ', was: 'Услуги', hint: 'Прайс услуг склада', icon: Wrench, section: 'services_tz' }),
        ...staff({ path: '/product-services-tz', label: 'ТЗ товаров', proc: 'Прайс и ТЗ', hint: 'Какие услуги делаем с каждым товаром: отпарить, переупаковать, промаркировать', icon: ClipboardList, section: 'services_tz' }),
      ],
    },
    {
      label: 'Настройки и справка',
      items: [
        { path: '/marketplace', label: 'Маркетплейсы', hint: 'Подключения к WB, Ozon, Яндекс Маркету', icon: Store, section: 'marketplace' },
        { path: '/shipping-warehouses', label: 'Склады отгрузки', hint: 'Адреса складов маркетплейсов и цена короба', icon: Truck, section: 'shipping' },
        { path: '/label-templates', label: 'Шаблоны этикеток', icon: Tags, section: 'label_templates' },
        ...staff({ path: '/printers', label: 'Принтеры', proc: 'Оборудование', icon: Printer, section: 'equipment' }),
        ...staff({ path: '/tsd', label: 'Терминалы (ТСД)', proc: 'Оборудование', icon: Smartphone, section: 'equipment' }),
        ...admin({ path: '/users', label: 'Сотрудники', proc: 'Люди и доступы', procIcon: Users, icon: Users }),
        ...staff({ path: '/staff-access', label: 'Персонал и доступы', proc: 'Люди и доступы', icon: Shield, section: 'staff_access' }),
        // «Расходники» — только глобальный admin (внутренний учёт склада)
        ...staff({ path: '/consumables', label: 'Расходники', icon: Boxes, section: 'consumables' }),
        ...staff({ path: '/admin/notifications', label: 'Уведомления и бот', icon: Bell, section: 'notifications' }),
        ...admin({ path: '/admin/ai', label: 'ИИ / OpenRouter', icon: Cpu }),
        ...admin({ path: '/settings/requisites', label: 'Реквизиты', proc: 'Склад и документы', hint: sel('Исполнитель в актах для клиентов: наименование, ИНН, банк, подписант', 'Название, ИНН, банк и подписант — для документов'), icon: FileText, keywords: 'реквизиты инн акт исполнитель' }),
        ...(isAdmin && isCloud ? [{ path: '/settings/brand', label: 'Бренд и домен', proc: 'Склад и документы', hint: 'Логотип, название, цвета и свой адрес склада', icon: Palette, keywords: 'бренд логотип домен цвет' }] : []),
        ...(isAdmin && isCloud ? [{ path: '/start', label: 'С чего начать', proc: 'Первые шаги', hint: 'Первые действия на новом складе и чек-лист настройки', icon: Rocket, keywords: 'начало старт первые шаги главная' }] : []),
        ...(isAdmin && isCloud ? [{ path: '/setup', label: 'Мастер настройки', proc: 'Первые шаги', hint: 'Настройка склада по шагам под ваш бизнес: компания, ячейки, товары, клиенты, сотрудники, маркетплейсы, печать', icon: Rocket, keywords: 'мастер настройка запуск начало' }] : []),
        // 28.09: «Каталог модулей» — склад облака сам включает нужное из тарифа
        ...(isAdmin && isCloud ? [{ path: '/subscription', label: 'Тариф и оплата', proc: 'Склад и документы', hint: 'Ваш тариф, сколько мест занято, оплата картой или по счёту, счета и акты', icon: CreditCard, keywords: 'тариф оплата счёт подписка продлить цена акт' }] : []),
        ...(isAdmin && isCloud ? [{ path: '/modules', label: 'Каталог модулей', proc: 'Склад и документы', hint: 'Какие разделы нужны складу: маркетплейсы, ЧЗ, клиенты, бот, API — включить или выключить', icon: Blocks, keywords: 'модули магазин интеграции подключить включить выключить тариф' }] : []),
        { path: '/knowledge', label: 'База знаний', hint: 'Инструкции по работе в системе', icon: HelpCircle, keywords: 'справка помощь' },
        // Облако Н3: склады облака, тарифы, пробный период — только владелец Full-Fix
        ...(isPlatformOwner ? [{ path: '/platform-admin', label: 'Кабинет платформы', hint: 'Склады облака: тариф, опции, пробный период, приостановка', icon: Cloud, keywords: 'облако операторы тарифы склады платформа' }] : []),
      ],
    },
    // Отдельный блок по решению владельца 26.09: не сливать, ждёт полной переделки
    {
      label: 'Продажи и прочее',
      items: [
        // CRM (модернизация 10.2026). Внутри раздела — своя навигация (CrmShell): там же
        // «Руководителю», «Журнал», «Корзина», «Настройки» по правам.
        { path: '/crm/home', label: 'CRM: мой день', hint: 'Мои лиды, задачи и сделки на сегодня', icon: Briefcase, section: 'crm', keywords: 'crm продажи' },
        { path: '/crm', label: 'Сделки', hint: 'CRM: доска сделок', icon: Briefcase, section: 'crm', keywords: 'crm воронка канбан' },
        { path: '/crm/leads', label: 'Лиды', hint: 'CRM: входящие заявки', icon: Inbox, section: 'crm', keywords: 'crm заявки' },
        { path: '/crm/tasks', label: 'Задачи CRM', hint: 'CRM: задачи и напоминания', icon: ClipboardList, section: 'crm', keywords: 'crm задачи' },
        { path: '/crm/contacts', label: 'Контакты', hint: 'CRM: люди', icon: Contact, section: 'crm', keywords: 'crm' },
        { path: '/crm/organizations', label: 'Организации', hint: 'CRM: компании-контрагенты', icon: Building2, section: 'crm', keywords: 'crm клиенты' },
        { path: '/crm/reports', label: 'Отчёты CRM', icon: PieChart, section: 'crm', keywords: 'crm отчёты конструктор' },
        ...staff({ path: '/crm/settings', label: 'Настройки CRM', icon: Settings2, section: 'crm_settings', keywords: 'crm воронки этапы источники' }),
        { path: '/marketing', label: 'Маркетинг', hint: 'Каналы, расходы, цена заявки, скорость реакции', icon: Megaphone, section: 'marketing', keywords: 'маркетинг реклама директ seo метрика лиды каналы расходы' },
        ...staff({ path: '/calculator', label: 'Калькулятор WB', icon: Calculator, section: 'calculator' }),
        ...staff({ path: '/calculation', label: 'Расчёт стоимости', icon: Receipt, section: 'calculation' }),
        ...staff({ path: '/fbs-calculator', label: 'Калькулятор ФБС', icon: Receipt, section: 'calculation', hint: 'Цена сборки ФБС от труда и затрат, КП и сетка клиенту' }),
        ...staff({ path: '/admin/knowledge-base', label: 'Редактор базы знаний', icon: BookOpen, keywords: 'база знаний статьи', section: 'kb_editor' }),
      ],
    },
  ];
  return sellerCloud ? sellerLabels(nav) : nav;
}

// Экраны, на которых кладовщик проводит смену: сверху, всегда раскрыты, под
// прежними названиями (was). В группах ниже их вкладки у него не повторяются.
// «Подбор» и «Сканирование» убраны 26.09 (решение владельца): скан и лист
// подбора — в карточке отгрузки; «Подбор» остался плиткой «Моих задач».
export const WAREHOUSE_WORK_PATHS = [
  '/work', '/board', '/arrivals', '/box-placement', '/placement', '/fbs-assembly',
];

// Экраны не из меню — находятся только поиском (Ctrl/⌘+K, «Все разделы»).
// Скан и лист подбора живут в карточке отгрузки, но старые экраны работают:
// кто помнит «Сканирование», должен найти его поиском. Права — как у маршрута
// (Layout проверяет section и canOpen). Только складу: клиенту они закрыты.
export const SEARCH_ONLY_ITEMS = [
  { path: '/scanning', label: 'Сканирование', hint: 'Скан товара и кодов ЧЗ в отгрузку (то же — в карточке отгрузки)', icon: ScanLine, section: 'scanning', keywords: 'скан сканирование штрихкод' },
  { path: '/picking', label: 'Лист подбора', was: 'Подбор', hint: 'Что и из каких ячеек собрать под отгрузки (то же — в карточке отгрузки)', icon: ClipboardList, section: 'picking', keywords: 'подбор лист подбора сборка' },
];

// Адрес не из меню → вкладка, которую подсвечивать (самой вкладки в меню нет,
// а человек работает внутри этого раздела). Дочерние адреса (/products/5,
// /arrivals/7/boxes) находит правило «самое длинное совпадение начала адреса».
export const PATH_SECTION_ALIASES = {
  '/orders': '/processing',
  '/orders/create': '/processing',
  '/shipments/create': '/processing',
  '/scanning': '/processing',
  '/picking': '/processing',
  '/otg-return': '/cell-audit',
  '/pallet-audit': '/cell-audit',
  '/payroll': '/finances',
};

/**
 * Какая вкладка меню «владеет» открытым адресом: точное совпадение (с
 * параметрами вкладки, если они есть) → карта PATH_SECTION_ALIASES → самое
 * длинное совпадение начала адреса. paths — адреса вкладок, видимых в меню.
 * Одно правило и для подсветки пункта, и для точки на свёрнутой группе.
 */
export function activeTabPath(pathname, search, paths) {
  const cur = new URLSearchParams(search || '');
  const base = (p) => p.split('?')[0];
  const exact = paths.filter((p) => {
    const [b, q] = p.split('?');
    if (b !== pathname) return false;
    return !q || [...new URLSearchParams(q)].every(([k, v]) => cur.get(k) === v);
  });
  // Вкладка с параметрами точнее вкладки без них
  if (exact.length) return exact.sort((a, b) => b.length - a.length)[0];
  const sameBase = paths.find((p) => base(p) === pathname);
  if (sameBase) return sameBase;
  const alias = PATH_SECTION_ALIASES[pathname];
  if (alias && paths.includes(alias)) return alias;
  const prefix = paths
    .filter((p) => pathname.startsWith(base(p) + '/'))
    .sort((a, b) => base(b).length - base(a).length)[0];
  return prefix || null;
}

// Меню клиента: главная + 3 группы процессов
export function buildClientNav() {
  return [
    {
      label: '',
      items: [
        { path: '/dashboard', label: 'Главная', icon: BarChart3 },
        { path: '/tasks', label: 'Задачи складу', hint: 'Попросить склад что-то сделать и следить, как идёт', icon: ClipboardCheck, keywords: 'задача просьба поручение' },
        { path: '/onboarding', label: 'Настройка кабинета', hint: 'Короткий квиз и уроки: настроим кабинет под вас', icon: Sparkles, companyAdminOnly: true, keywords: 'квиз обучение начало' },
      ],
    },
    {
      label: 'Поставки',
      items: [
        { path: '/arrivals', label: 'Приёмки', proc: 'Приёмки', hint: 'Товар, который вы привозите на склад', icon: PackageCheck, section: 'arrivals' },
        { path: '/pickups', label: 'Заборы', proc: 'Приёмки', hint: 'Заявки на забор товара у вас', icon: Truck, section: 'arrivals' },
        { path: '/processing', label: 'Отгрузки', hint: 'Поставки на маркетплейсы; «+ Создать отгрузку»', icon: ClipboardList, section: 'processing' },
        { path: '/orders/create', label: 'Создать отгрузку', action: true, menuFallback: '/processing', hint: 'Новая отгрузка на маркетплейс или самовывоз', icon: PlusCircle, section: 'orders', keywords: 'создать поставку' },
        { path: '/fbs', label: 'Заказы', proc: 'Заказы ФБС', was: 'Заказы ФБС', hint: 'Заказы с маркетплейсов, которые мы собираем и отвозим сами', icon: Package, section: 'orders' },
        { path: '/fbs-dashboard', label: 'Сроки', proc: 'Заказы ФБС', was: 'ФБС Контроль', hint: 'Успеваем ли собрать заказы к выезду машины', icon: BarChart3, section: 'orders' },
        // «Рейсы» у клиента убраны (аудит №301): раздел внутренний
        { path: '/pvz-returns', label: 'Возвраты ПВЗ', hint: 'Товар, вернувшийся из пунктов выдачи', icon: Undo2, section: 'processing' },
      ],
    },
    {
      label: 'Товары',
      items: [
        // «Остатки» — первой: главный экран клиента (766 открытий за 14 дней против 145 у каталога)
        { path: '/warehouse', label: 'Остатки', proc: 'Остатки и каталог', hint: 'Сколько вашего товара на складе', icon: Boxes, section: 'products' },
        { path: '/products', label: 'Каталог', proc: 'Остатки и каталог', hint: 'Ваши товары', icon: ShoppingBag, section: 'products', keywords: 'товары' },
        { path: '/stock-transfer', label: 'Перенос остатка', proc: 'Остатки и каталог', hint: 'Попросить склад перекинуть остаток с одной вашей карточки на другую', icon: ArrowRightLeft, section: 'products', keywords: 'перенос перемещение карточка дубль остаток заявка' },
        { path: '/product-management', label: 'Загрузка Excel', proc: 'Остатки и каталог', was: 'Управление товарами', hint: 'Загрузка и массовое изменение товаров из Excel', icon: Settings2, section: 'products', keywords: 'управление' },
        { path: '/barcodes', label: 'Баркоды в Excel', proc: 'Остатки и каталог', was: 'Баркоды', hint: 'Дописать колонку баркода в свой Excel по артикулу и размеру', icon: Barcode, section: 'products', keywords: 'штрихкоды шк' },
        { path: '/defects', label: 'Брак / Пересорт', hint: 'Товар с дефектом или пересортица — оформление и списание', icon: AlertTriangle, section: 'processing' },
        { path: '/product-services-tz', label: 'ТЗ товаров', hint: 'Какие услуги делаем с каждым товаром: отпарить, переупаковать, промаркировать', icon: ClipboardList, companyAdminOnly: true },
        { path: '/marking', label: 'Коды ЧЗ', proc: 'Маркировка', was: 'Маркировка', hint: 'Честные знаки: загрузка, печать, привязка к товару', icon: ScanBarcode, section: 'marking', keywords: 'чз киз честный знак' },
        { path: '/label-templates', label: 'Этикетки', proc: 'Маркировка', hint: 'Шаблоны этикеток', icon: Tags, section: 'marking' },
        { path: '/my-consumables', label: 'Расходники', icon: PackageOpen, companyAdminOnly: true },
      ],
    },
    {
      label: 'Компания и деньги',
      items: [
        { path: '/finance', label: 'Начисления', proc: 'Финансы', was: 'Финансы', hint: 'Баланс, начисления, акты', icon: Banknote, companyAdminOnly: true, keywords: 'баланс акты' },
        { path: '/storage-billing', label: 'Хранение', proc: 'Финансы', hint: 'Начисления за хранение', icon: Warehouse, companyAdminOnly: true },
        { path: '/companies', label: 'Данные', proc: 'Компания', procIcon: Building2, was: 'Компании', hint: 'Реквизиты и пользователи', icon: Building2, companyAdminOnly: true },
        { path: '/marketplace', label: 'Маркетплейсы', proc: 'Компания', hint: 'Подключения к WB, Ozon, Яндекс Маркету', icon: Store, companyAdminOnly: true },
        { path: '/integrations/api', label: 'API', proc: 'Компания', hint: 'Ключ для подключения вашей системы', icon: Key, companyAdminOnly: true },
        { path: '/knowledge', label: 'База знаний', hint: 'Инструкции по работе в системе', icon: HelpCircle, keywords: 'справка помощь' },
      ],
    },
  ];
}

/**
 * Вкладки → пункты меню. На входе — группы с уже отфильтрованными по правам
 * вкладками; на выходе у каждой группы entries: [{ key, path, label, icon, hint,
 * tabs }]. Процесс собирается из вкладок с тем же proc в пределах группы, в
 * порядке первой из них. Действия (action) в пункты не попадают, если виден их
 * экран menuFallback (visiblePaths) — там кнопка.
 *
 * splitPaths — вкладки, вынесенные в «Мою работу» кладовщика: процесс, у
 * которого часть вкладок ушла туда, распадается на отдельные пункты под
 * прежними названиями, иначе в меню стояло бы два «Приёмка» с разным смыслом.
 */
export function buildMenuEntries(sections, { visiblePaths, splitPaths } = {}) {
  const vis = visiblePaths || new Set();
  const split = splitPaths || new Set();
  return sections.map((sec) => {
    const entries = [];
    const byProc = {};
    // Отдельный пункт: у распавшегося процесса — под прежним названием (was)
    const single = (t, useOld) => ({
      key: t.path, path: t.path, label: useOld ? (t.was || t.label) : t.label,
      icon: t.icon, hint: t.hint, tabs: [t],
    });
    const procHasSplit = {};
    sec.items.forEach((t) => { if (t.proc && split.has(t.path)) procHasSplit[t.proc] = true; });
    sec.items.forEach((t) => {
      if (split.has(t.path)) return;
      if (t.action) {
        if (!(t.menuFallback && vis.has(t.menuFallback))) entries.push(single(t, false));
        return;
      }
      if (!t.proc || procHasSplit[t.proc]) { entries.push(single(t, !!t.proc)); return; }
      let e = byProc[t.proc];
      if (!e) {
        e = { key: `proc:${t.proc}`, proc: t.proc, path: t.path, label: t.proc, icon: t.procIcon || t.icon, hint: t.hint, tabs: [] };
        byProc[t.proc] = e;
        entries.push(e);
      }
      if (t.procIcon) e.icon = t.procIcon;
      e.tabs.push(t);
    });
    entries.forEach((e) => {
      if (e.tabs.length > 1) {
        e.hint = e.tabs.map((t) => t.label).join(' · ');
      } else if (e.proc) {
        // Из процесса видна одна вкладка — пункт под её прежним названием:
        // упаковщику с одной «Сводкой отгрузок» пункт «Склад сейчас» соврал бы
        const t = e.tabs[0];
        e.label = t.was || t.label;
        e.icon = t.icon;
      }
    });
    return { ...sec, entries };
  }).filter((sec) => sec.entries.length > 0);
}

/** Название вкладки для поиска и «Всех разделов»: «Приёмка · Забор». */
export function searchLabel(t) {
  if (t.proc && t.proc !== t.label) return `${t.proc} · ${t.label}`;
  return t.label;
}

/** Вкладки как строки поиска: прежнее название — ключевым словом. */
export function searchSections(sections) {
  return sections.map((sec) => ({
    ...sec,
    items: sec.items.map((t) => ({
      ...t,
      label: searchLabel(t),
      keywords: [t.keywords, t.was, t.proc].filter(Boolean).join(' '),
    })),
  }));
}

// Тестовые компании в шапке меню не показываем («E2E Labels Test» и подобные)
const TEST_NAME = /(^|[^a-zа-яё])(e2e|test|тест)/i;
export const isTestCompany = (c) => !!c && TEST_NAME.test(c.name || '');

// Поиск: без регистра, ё = е
export const normalize = (s) => (s || '').toLowerCase().replace(/ё/g, 'е');
export function matchItem(item, query, groupLabel = '') {
  const q = normalize(query).trim();
  if (!q) return true;
  const hay = normalize([item.label, item.hint, item.keywords, groupLabel].filter(Boolean).join(' '));
  return q.split(/\s+/).every((w) => hay.includes(w));
}

// Порядок выдачи поиска: совпало название — выше, чем совпала подсказка или
// ключевое слово («подбор» → сперва «Лист подбора», потом всё, где он упомянут).
export function matchRank(item, query) {
  const words = normalize(query).trim().split(/\s+/).filter(Boolean);
  const label = normalize(item.label);
  if (words.every((w) => label.includes(w))) return label.startsWith(words[0]) ? 0 : 1;
  const kw = normalize([item.keywords, item.was].filter(Boolean).join(' '));
  if (words.every((w) => kw.includes(w) || label.includes(w))) return 2;
  return 3;
}
